import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useSite } from "@/context/SiteContext";
import { photoSrc } from "@/lib/api";

const Lightbox = ({ photos, index, setIndex }) => {
  const touch = useRef(null);
  const open = index !== null;
  const step = (d) => setIndex((i) => (i + d + photos.length) % photos.length);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const p = open ? photos[index] : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent className="max-w-5xl p-0 bg-primary border-0 [&>button]:hidden" data-testid="gallery-lightbox"
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1); }}>
        <DialogTitle className="sr-only">Photo {index + 1} of {photos.length}</DialogTitle>
        {p && <img src={photoSrc(p.url)} alt={p.caption} className="w-full max-h-[80vh] object-contain" />}
        <div className="flex items-center justify-between text-primary-foreground px-4 py-3 text-sm">
          <button onClick={() => step(-1)} aria-label="Previous photo" data-testid="lightbox-prev"><ChevronLeft /></button>
          <span>{p?.caption} · {index + 1}/{photos.length}</span>
          <div className="flex gap-4">
            <button onClick={() => step(1)} aria-label="Next photo" data-testid="lightbox-next"><ChevronRight /></button>
            <button onClick={() => setIndex(null)} aria-label="Close" data-testid="lightbox-close"><X /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Gallery = () => {
  const { site } = useSite();
  const photos = site?.photos || [];
  const [index, setIndex] = useState(null);
  if (!photos.length) return null;
  const [first, ...rest] = photos;
  return (
    <section id="gallery" className="section-pad bg-white border-y border-border scroll-mt-16" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div><p className="eyebrow">Gallery</p><h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-medium">A look inside</h2></div>
          <button onClick={() => setIndex(0)} className="text-sm underline underline-offset-4" data-testid="gallery-view-all">View all {photos.length} photos</button>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <button onClick={() => setIndex(0)} className="col-span-2 row-span-2 aspect-square overflow-hidden rounded-lg group" data-testid="gallery-featured">
            <img src={photoSrc(first.url)} alt={first.caption} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          </button>
          {rest.slice(0, 4).map((p, i) => (
            <button key={p.id} onClick={() => setIndex(i + 1)} className="aspect-square overflow-hidden rounded-lg group" data-testid={`gallery-thumb-${i}`}>
              <img src={photoSrc(p.url)} alt={p.caption} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            </button>
          ))}
        </div>
      </div>
      <Lightbox photos={photos} index={index} setIndex={setIndex} />
    </section>
  );
};
